import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { MovieData, MovieState, MOVIE_TYPE } from '@/types/movie'

interface ExtendedMovieState extends MovieState {
  selectedMovieId: number | null
  isModalOpen: boolean
}

const initialState: ExtendedMovieState = {
  movies: [],
  selectedMovieId: null,
  isModalOpen: false,
}

/**
 * Extract movie ID and parameters from URL
 */
function extractMovieIdFromUrl(
  url: string,
  movieType: number,
): { movieId: string; params?: string } {
  const decodedUrl = decodeURIComponent(url)
  const paramsRegex = /(\?|#|&)/

  if (movieType === MOVIE_TYPE.YOUTUBE) {
    // YouTube URL patterns
    let param = 'watch?v='
    if (decodedUrl.includes('youtu.be')) param = 'youtu.be/'
    if (decodedUrl.includes('embed/')) param = 'embed/'

    const idStartNum = decodedUrl.indexOf(param) + param.length
    const cutOutId = decodedUrl.substring(idStartNum)

    if (cutOutId.match(paramsRegex)) {
      const params = cutOutId.substring(cutOutId.search(paramsRegex))
      return {
        movieId: cutOutId.substring(0, cutOutId.search(paramsRegex)),
        params: '?' + params.substring(1),
      }
    }
    return { movieId: cutOutId }
  } else {
    // Vimeo URL patterns
    const idStartNum = decodedUrl.lastIndexOf('/') + 1
    const allInMovieId = decodedUrl.substring(idStartNum)

    if (allInMovieId.match(paramsRegex)) {
      return {
        movieId: allInMovieId.substring(0, allInMovieId.search(paramsRegex)),
        params: allInMovieId.substring(allInMovieId.search(paramsRegex)),
      }
    }
    return { movieId: allInMovieId }
  }
}

/**
 * Generate iframe URL based on movie type
 */
function generateIframeUrl(movieId: string, movieType: number, params?: string): string {
  if (movieType === MOVIE_TYPE.YOUTUBE) {
    if (params) {
      return `https://www.youtube.com/embed/${movieId}${params}&autoplay=1`
    }
    return `https://www.youtube.com/embed/${movieId}?autoplay=1`
  } else {
    // Vimeo
    if (params) {
      if (params[0] !== '#') {
        return `https://player.vimeo.com/video/${movieId}?autoplay=1&${params.slice(1)}`
      }
      return `https://player.vimeo.com/video/${movieId}?autoplay=1${params}`
    }
    return `https://player.vimeo.com/video/${movieId}?autoplay=1`
  }
}

/**
 * Generate thumbnail URL based on movie type
 */
function generateThumbnailUrl(movieId: string, movieType: number): string {
  if (movieType === MOVIE_TYPE.YOUTUBE) {
    return `https://img.youtube.com/vi/${movieId}/hqdefault.jpg`
  }
  // For Vimeo, thumbnail needs to be fetched separately via API
  return ''
}

/**
 * Transform movie data with proper iframe URLs
 */
function transformMovieData(movie: MovieData): MovieData {
  const { movieId, params } = extractMovieIdFromUrl(movie.url, movie.movieType)

  return {
    ...movie,
    movieId,
    params,
    iframeUrl: generateIframeUrl(movieId, movie.movieType, params),
    thumbnail:
      movie.thumbnail || movie.thumbnailUrl || generateThumbnailUrl(movieId, movie.movieType),
  }
}

const movieSlice = createSlice({
  name: 'movie',
  initialState,
  reducers: {
    setMovie: (state, action: PayloadAction<MovieData>) => {
      const transformedMovie = transformMovieData(action.payload)
      const existingMovie = state.movies.find((movie) => movie.id === transformedMovie.id)
      if (!existingMovie) {
        state.movies.push(transformedMovie)
        state.movies.sort((a, b) => a.id - b.id)
      }
    },
    setMovies: (state, action: PayloadAction<MovieData[]>) => {
      state.movies = action.payload.map(transformMovieData).sort((a, b) => a.id - b.id)
    },
    clearMovies: (state) => {
      state.movies = []
      state.selectedMovieId = null
      state.isModalOpen = false
    },
    openMovieModal: (state, action: PayloadAction<number>) => {
      state.selectedMovieId = action.payload
      state.isModalOpen = true
    },
    closeMovieModal: (state) => {
      state.selectedMovieId = null
      state.isModalOpen = false
    },
  },
})

export const { setMovie, setMovies, clearMovies, openMovieModal, closeMovieModal } =
  movieSlice.actions
export default movieSlice.reducer
